import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Upload, Link2, Loader2, Trash2, Plus, GripVertical,
  Image, Eye, EyeOff, Check, X as XIcon
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#1E1E3A]/80 backdrop-blur-xl shadow-lg ${className}`} {...props}>{children}</div>
);

export const ImageUploader = ({ value, onChange, label, hint, blockId, linkValue, onLinkChange }) => {
  const [uploading, setUploading] = useState(false);
  const [showLink, setShowLink] = useState(!!linkValue);
  const ref = useRef();
  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('category', 'content');
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }});
      const url = `${API}/media/files/${res.data.id}`; onChange(url);
    } catch { onChange(''); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-[#9CA3AF] text-xs">{label || 'Image'}</Label>
      {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
      {value ? (
        <div className="relative group">
          <img src={value} alt="" className="w-full h-28 object-cover rounded-xl border border-white/10" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl flex items-center justify-center gap-2 transition-opacity">
            <button onClick={() => ref.current?.click()} className="p-1.5 bg-white/20 rounded-lg text-white hover:bg-white/30"><Upload size={12} /></button>
            <button onClick={() => setShowLink(!showLink)} className="p-1.5 bg-white/20 rounded-lg text-white hover:bg-white/30"><Link2 size={12} /></button>
            <button onClick={() => onChange('')} className="p-1.5 bg-red-500/40 rounded-lg text-white hover:bg-red-500/60"><Trash2 size={12} /></button>
          </div>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} className="w-full h-24 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center text-[#64748B] hover:border-[#8B5CF6]/30 transition-colors" disabled={uploading}>
          {uploading ? <Loader2 size={16} className="animate-spin text-[#8B5CF6]" /> : <><Upload size={16} /><span className="text-[10px] mt-1">Upload {label || 'image'}</span></>}
        </button>
      )}
      {showLink && onLinkChange && (
        <div className="flex items-center gap-1.5">
          <Link2 size={12} className="text-[#64748B] shrink-0" />
          <Input value={linkValue || ''} onChange={(e) => onLinkChange(e.target.value)} placeholder="https://..." className="bg-white/5 border-white/10 text-white text-xs rounded-lg h-7" />
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`img-upload-${blockId}`} />
    </div>
  );
};

/**
 * FocalPointPicker — click anywhere on the preview to set the (x%, y%) focal
 * point. The hero/banner renderer uses `objectPosition: "${x}% ${y}%"` so the
 * chosen part of the image stays visible as the container is cropped.
 */
export const FocalPointPicker = ({ imageUrl, value, onChange, label = 'Focal Point', aspect = '16 / 6' }) => {
  const ref = useRef(null);
  const fx = typeof value?.x === 'number' ? value.x : 50;
  const fy = typeof value?.y === 'number' ? value.y : 50;

  const handlePick = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    onChange({ x: Math.round(x), y: Math.round(y) });
  };

  if (!imageUrl) {
    return (
      <div className="space-y-1.5">
        <Label className="text-[#9CA3AF] text-xs">{label}</Label>
        <p className="text-[10px] text-slate-500">Upload an image first to set its focal point.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="focal-point-picker">
      <div className="flex items-center justify-between">
        <Label className="text-[#9CA3AF] text-xs">{label}</Label>
        <span className="text-[10px] text-slate-500 font-mono">{fx}%, {fy}%</span>
      </div>
      <p className="text-[10px] text-slate-500">Click any point to mark what stays in view when the image is cropped by the banner.</p>
      <div
        ref={ref}
        onClick={handlePick}
        className="relative w-full rounded-xl overflow-hidden border border-white/10 cursor-crosshair bg-black/20 select-none"
        style={{ aspectRatio: aspect }}
        data-testid="focal-point-canvas"
      >
        <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)' }} />
        <div
          className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_20px_rgba(255,255,255,0.6)] pointer-events-none"
          style={{ left: `${fx}%`, top: `${fy}%`, background: 'radial-gradient(circle, rgba(139,92,246,0.9) 0%, rgba(139,92,246,0.2) 70%, transparent 100%)' }}
          data-testid="focal-point-marker"
        >
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white/60" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/60" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1 text-[10px]">
        {[
          ['Top-L', 20, 20], ['Top-C', 50, 20], ['Top-R', 80, 20],
          ['Mid-L', 20, 50], ['Center', 50, 50], ['Mid-R', 80, 50],
          ['Bot-L', 20, 80], ['Bot-C', 50, 80], ['Bot-R', 80, 80],
        ].map(([name, px, py]) => (
          <button
            key={name}
            type="button"
            onClick={() => onChange({ x: px, y: py })}
            className={`py-1 rounded border transition-all ${fx === px && fy === py ? 'border-[#8B5CF6] bg-[#8B5CF6]/15 text-white' : 'border-white/10 bg-white/5 text-[#9CA3AF] hover:border-white/20'}`}
            data-testid={`focal-preset-${name.toLowerCase()}`}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
};



export const VideoUploader = ({ value, onChange, blockId }) => {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('category', 'video');
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }});
      onChange(`${API}/media/files/${res.data.id}`);
    } catch {} finally { setUploading(false); }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-[#9CA3AF] text-xs">Video</Label>
      {value ? (
        <div className="relative group">
          <video src={value} className="w-full h-20 object-cover rounded-xl border border-white/10" />
          <button onClick={() => onChange('')} className="absolute top-1 right-1 p-1 bg-red-500/60 rounded-full text-white"><Trash2 size={10} /></button>
        </div>
      ) : (
        <button onClick={() => ref.current?.click()} className="w-full h-16 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center text-[#64748B] hover:border-[#8B5CF6]/30" disabled={uploading}>
          {uploading ? <Loader2 size={14} className="animate-spin text-[#8B5CF6]" /> : <><Upload size={14} /><span className="text-[10px] mt-0.5">Upload video</span></>}
        </button>
      )}
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Or paste URL..." className="bg-white/5 border-white/10 text-white text-xs rounded-lg" />
      <input ref={ref} type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`vid-upload-${blockId}`} />
    </div>
  );
};

export const GalleryEditor = ({ images = [], onChange, layout = 'grid', onLayoutChange, blockId }) => {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const addImage = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('category', 'gallery');
    try {
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }});
      onChange([...images, `${API}/media/files/${res.data.id}`]);
    } catch {} finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[#9CA3AF] text-xs">Gallery ({images.length}/50)</Label>
        <select value={layout} onChange={(e) => onLayoutChange?.(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded px-2 py-0.5 text-[10px]">
          <option value="grid">Grid</option><option value="masonry">Masonry</option><option value="carousel">Carousel</option>
        </select>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {images.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img} alt="" className="w-full h-14 object-cover rounded-lg border border-white/10" />
            <button onClick={() => onChange(images.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"><XIcon size={8} /></button>
          </div>
        ))}
        {images.length < 50 && (
          <button onClick={() => ref.current?.click()} className="h-14 border border-dashed border-white/10 rounded-lg flex items-center justify-center text-[#64748B] hover:border-[#8B5CF6]/30" disabled={uploading}>
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && addImage(e.target.files[0])} data-testid={`gallery-upload-${blockId}`} />
    </div>
  );
};

export const ButtonsEditor = ({ buttons = [], onChange, blockId }) => (
  <div className="space-y-2 pt-2 border-t border-white/5">
    <Label className="text-[#9CA3AF] text-[10px] uppercase tracking-wider">Buttons / CTAs</Label>
    {buttons.map((btn, i) => (
      <div key={i} className="flex gap-1.5">
        <Input value={btn.text || ''} onChange={(e) => { const nb = [...buttons]; nb[i] = { ...nb[i], text: e.target.value }; onChange(nb); }} placeholder="Label" className="bg-white/5 border-white/10 text-white text-xs rounded-lg flex-1" />
        <Input value={btn.link || ''} onChange={(e) => { const nb = [...buttons]; nb[i] = { ...nb[i], link: e.target.value }; onChange(nb); }} placeholder="URL" className="bg-white/5 border-white/10 text-white text-xs rounded-lg flex-1" />
        <select value={btn.style || 'filled'} onChange={(e) => { const nb = [...buttons]; nb[i] = { ...nb[i], style: e.target.value }; onChange(nb); }} className="bg-white/5 border border-white/10 text-white rounded-lg px-1.5 text-[10px] w-16">
          <option value="filled">Filled</option><option value="outline">Outline</option><option value="ghost">Ghost</option>
        </select>
        <button onClick={() => onChange(buttons.filter((_, idx) => idx !== i))} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
      </div>
    ))}
    <button onClick={() => onChange([...buttons, { text: 'Button', link: '#', style: 'filled' }])} className="flex items-center gap-1 text-[10px] text-[#A78BFA] hover:text-[#C4B5FD]" data-testid={`add-btn-${blockId}`}>
      <Plus size={10} /> Add Button
    </button>
  </div>
);

export function SortableBlock({ block, isActive, onToggle, onDelete, onToggleVis, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <GlassCard className={`transition-all ${isActive ? 'ring-1 ring-[#8B5CF6]/50' : ''} ${!block.is_visible ? 'opacity-40' : ''}`}>
        <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={onToggle} data-testid={`block-header-${block.id}`}>
          <button {...listeners} className="p-0.5 text-[#64748B] hover:text-white cursor-grab" onClick={(e) => e.stopPropagation()}><GripVertical size={14} /></button>
          <span className="text-xs font-medium text-[#94A3B8] flex-1 truncate">{block.type}</span>
          <button onClick={(e) => { e.stopPropagation(); onToggleVis(); }} className="p-1 text-[#64748B] hover:text-white">{block.is_visible ? <Eye size={12} /> : <EyeOff size={12} />}</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 text-[#64748B] hover:text-red-400"><Trash2 size={12} /></button>
        </div>
        {isActive && <div className="px-3 pb-3">{children}</div>}
      </GlassCard>
    </div>
  );
}

export function SortableFeaturedProduct({ product, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-2 p-1.5 rounded-lg bg-white/5 border border-white/10 group">
      <button {...listeners} className="text-[#64748B] hover:text-white cursor-grab"><GripVertical size={12} /></button>
      {product.images?.[0] && <img src={product.images[0]} alt="" className="w-7 h-7 rounded object-cover" />}
      <span className="text-[11px] text-[#94A3B8] flex-1 truncate">{product.name}</span>
      <span className="text-[10px] text-[#00CC99]">${product.price}</span>
      <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 p-0.5 text-red-400"><XIcon size={10} /></button>
    </div>
  );
}
